import ENDPOINTS from "./Endpoints";
import { nutClientForRole } from "./nutClient";
import {
  ExerciseGroupV2Model,
  ExerciseV1Model,
  ScheduledService,
  WeeklyWorkoutOverviewResponse,
  WorkoutAnalysisType,
} from "../../modules/platform/components/programming/workoutPlan/model/workoutPlanTypes";

/**
 * Weekly workout plan endpoints on the NUT service, the same GETs the
 * mobile WorkoutTab reads (PrecisionReactNative `workouts/stores/api.ts`).
 * Read-only: the move / complete / log / swap mutations are not ported.
 *
 * Every call is authenticated as the member whose plan is shown: the
 * customer's own token on `/platform`, the client token minted into the tab
 * on `/client` (`nutClientForRole`).
 */

/** The Monday-based week containing `date`. Pass the Monday itself, as the mobile app does. */
export const getWeeklyWorkoutOverview = async (
  date: string,
  role: number
): Promise<WeeklyWorkoutOverviewResponse> => {
  const client = await nutClientForRole(role);
  const response = await client.get<WeeklyWorkoutOverviewResponse>(
    ENDPOINTS.NUTRITION.CUSTOMER_ACTIVITY_WEEKLY_OVERVIEW,
    { params: { date } }
  );
  return response.data;
};

/** Coached sessions/appointments in the week of `date` (a Monday). */
export const getWeeklyScheduledServices = async (
  date: string,
  role: number
): Promise<ScheduledService[]> => {
  const client = await nutClientForRole(role);
  const response = await client.get<ScheduledService[]>(
    ENDPOINTS.NUTRITION.CUSTOMER_SCHEDULED_SERVICES,
    { params: { date } }
  );
  return response.data;
};

/**
 * V2 detail: the workout's exercise groups. A bare array; empty (or a first
 * group without exercises) means the workout is a legacy V1 one. `date`
 * selects the day's exercise alternatives.
 */
export const getActivityExerciseGroups = async (
  activityId: string,
  date: string,
  role: number
): Promise<ExerciseGroupV2Model[]> => {
  const client = await nutClientForRole(role);
  const response = await client.get<ExerciseGroupV2Model[]>(
    ENDPOINTS.NUTRITION.CUSTOMER_ACTIVITY_EXERCISE_GROUPS(activityId),
    { params: { date } }
  );
  return response.data;
};

/** V1 detail: the legacy flat workout record. */
export const getActivityDetailsV1 = async (
  activityId: string,
  role: number
): Promise<ExerciseV1Model> => {
  const client = await nutClientForRole(role);
  const response = await client.get<ExerciseV1Model>(
    ENDPOINTS.NUTRITION.CUSTOMER_ACTIVITY_DETAILS(activityId)
  );
  return response.data;
};

/** Post-workout analysis of a recorded session: HR series, time per zone, fuel usage, PNOĒ effects. */
export const getWorkoutAnalysis = async (
  eventId: string,
  role: number
): Promise<WorkoutAnalysisType> => {
  const client = await nutClientForRole(role);
  const response = await client.get<WorkoutAnalysisType>(
    ENDPOINTS.NUTRITION.CUSTOMER_ACTIVITY_EVENT_ANALYSIS(eventId)
  );
  return response.data;
};
